import { createTheme } from "@mui/material/styles";
import type { PaletteOptions, PaletteMode } from "@mui/material";

// rgba alpha (last parameter) corresponds to 7th and 8th symbol in hex color code

const lightPalette: PaletteOptions = {
  // rgba(0, 0, 0, 0.06)
  divider: "#0000000f",
  background: {
    default: "#F9F4EF",
    paper: "#FFFFFF",
  },
  common: {
    white: "#ffffff",
    black: "#000000",
  },
  text: {
    primary: "#082042",
    secondary: "#626262",
    disabled: "#808080",
  },
  primary: {
    main: "#082042",
    dark: "#031732",
    light: "#FFC85C",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#E49B3D",
    dark: "#DB8A21",
    light: "#6A7A8E",
    contrastText: "#FFFFFF",
  },
  error: {
    main: "#CD5437",
    dark: "#B50808",
    light: "#F5DDD7",
    contrastText: "#FFFFFF",
  },
  warning: {
    main: "#E49B3D",
    dark: "#8F5300",
    light: "#E49B3D",
    contrastText: "#FFFFFF",
  },
  info: {
    main: "#135D95",
    dark: "#063F6A",
    light: "#0288D1",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#5EB273",
    dark: "#2F5B3A",
    light: "#D1F6C7",
    contrastText: "#FFFFFF",
  },
  action: {
    // rgba(0, 0, 0, 0.54)
    active: "#0000008a",
    // rgba(0, 0, 0, 0.04)
    hover: "#0000000a",
    // rgba(0, 0, 0, 0.08)
    selected: "#00000014",
    // rgba(0, 0, 0, 0.26)
    disabled: "#00000042",
    //rgba(0, 0, 0, 0.12);
    disabledBackground: "#0000001f",
    // rgba(0, 0, 0, 0.12)
    focus: "#0000001f",
  },
};

const darkPalette = {
  // rgba(255, 255, 255, 0.12)
  divider: "#ffffff1f",
  background: {
    default: "#F9F4EF",
    paper: "#121212",
  },
  common: {
    white: "#ffffff",
    black: "#000000",
  },
  text: {
    primary: "#FFFFFF",
    // rgba(255, 255, 255, 0.7)
    secondary: "#ffffffb3",
    // rgba(255, 255, 255, 0.5)
    disabled: "#ffffff80",
  },
  primary: {
    main: "#E49B3D",
    dark: "#031732",
    light: "rgba(8, 32, 64, 0.04)",
    contrastText: "#fff",
  },
  secondary: {
    main: "#89C9FB",
    dark: "#2071AE",
    light: "#CFEEFF",
    // rgba(0, 0, 0, 0.87)
    contrastText: "#000000de",
  },
  error: {
    main: "#F44336",
    dark: "#B50808",
    light: "#F5DDD7",
    contrastText: "#FFFFFF",
  },
  warning: {
    main: "#E49B3D",
    dark: "#8F5300",
    light: "#FFE0A5",
    // rgba(0, 0, 0, 0.87)
    contrastText: "#000000de",
  },
  info: {
    main: "#135D95",
    dark: "#063F6A",
    light: "#89C9FB",
    contrastText: "#000000de",
  },
  success: {
    main: "#5EB273",
    dark: "#2F5B3A",
    light: "#D1F6C7",
    // rgba(0, 0, 0, 0.87)
    contrastText: "#000000de",
  },
  action: {
    // rgba(255, 255, 255, 0.56)
    active: "#ffffff8f",
    // rgba(255, 255, 255, 0.08)
    hover: "#ffffff14",
    // rgba(255, 255, 255, 0.16)
    selected: "#ffffff29",
    // rgba(255, 255, 255, 0.3)
    disabled: "#ffffff4d",
    // rgba(255, 255, 255, 0.12)
    disabledBackground: "#ffffff1f",
    // rgba(255, 255, 255, 0.12)
    focus: "#ffffff1f",
  },
};

const typography = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  h1: {
    fontWeight: 400,
    fontSize: "6rem",
    letterSpacing: "-0.094rem",
  },
  h2: {
    fontWeight: 400,
    fontSize: "3.75rem",
    letterSpacing: "-0.031rem",
    lineHeight: "4.5rem",
  },
  h3: {
    fontWeight: 400,
    fontSize: "3rem",
  },
  h4: {
    fontWeight: 400,
    fontSize: "2.125rem",
    letterSpacing: "0.016rem",
    "@media (max-width:600px)": {
      fontSize: "1.5rem",
    },
  },
  h5: {
    fontWeight: 400,
    fontSize: "1.5rem",
  },
  h6: {
    fontWeight: 500,
    fontSize: "1.25rem",
    letterSpacing: "0.009rem",
    lineHeight: "2rem",
  },
  subtitle1: {
    fontWeight: 400,
    fontSize: "1rem",
    letterSpacing: "0.009rem",
    lineHeight: "1.75rem",
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: "0.875rem",
    letterSpacing: "0.006rem",
  },
  body1: {
    fontWeight: 400,
    fontSize: "1rem",
    letterSpacing: "0.009rem",
    lineHeight: "1.5rem",
  },
  body2: {
    fontWeight: 400,
    fontSize: "0.875rem",
    letterSpacing: "0.009rem",
  },
  button: {
    fontWeight: 500,
    fontSize: "0.875rem",
    letterSpacing: "0.125rem",
    textTransform: "uppercase" as const,
  },
  caption: {
    fontWeight: 400,
    fontSize: "0.875rem",
    letterSpacing: "0.011rem",
  },
  overline: {
    fontWeight: 400,
    fontSize: "0.875rem",
    letterSpacing: "0.011rem",
    textTransform: "uppercase" as const,
  },
};

const mode: PaletteMode = "light";

const theme = createTheme({
  typography,
  palette: {
    mode: "light",
    ...(mode === "light"
      ? // light mode
      lightPalette
      : // dark mode
      darkPalette),
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: "large",
      },
      styleOverrides: {
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#031732",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: "#F5F5F5",
          "& .MuiLinearProgress-barColorPrimary": {
            backgroundColor: "#082042",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: "relative",
          boxShadow: "0px 0px 5px #ddd;",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ":last-child": {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        filledInfo: {
          backgroundColor: "#135D95",
        },
        root: {
          "& .MuiAlertTitle-root": {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#031732",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

export default theme;
